import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { BanService } from "app/service/ban.service";
import { CheckMatAutoService } from "app/service/check-mat-auto.service";
import { debounceTime, tap, switchMap, finalize } from "rxjs/operators";
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from "@angular/material/core";
import { MomentDateAdapter } from "@angular/material-moment-adapter";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { InscriptionService } from "app/service/inscription.service";
import { ToastrService } from "ngx-toastr";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService } from "app/service/auth/auth.service";
import { NgxSpinnerService } from "ngx-spinner";
import emailMask from "text-mask-addons/dist/emailMask";
import Inscription from "app/core/models/inscription";
import { environment } from "environments/environment";
import { FileUploader } from "ng2-file-upload";
import * as moment from "moment";

export const MY_FORMATS = {
  parse: {
    dateInput: "DD/MM/YYYY",
  },
  display: {
    dateInput: "DD/MM/YYYY",
    monthYearLabel: "MMM YYYY",
    dateA11yLabel: "LL",
    monthYearA11yLabel: "MMMM YYYY",
  },
};

@Component({
  selector: "app-inscription-edit",
  templateUrl: "./inscription-edit.component.html",
  styleUrls: ["./inscription-edit.component.css"],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MY_FORMATS,
    },
    BanService,
    CheckMatAutoService,
  ],
})
export class InscriptionEditComponent implements OnInit {
  telmask = [
    /0/,
    /[1-9]/,
    "-",
    /[0-9]/,
    /[0-9]/,
    "-",
    /[0-9]/,
    /[0-9]/,
    "-",
    /[0-9]/,
    /[0-9]/,
    "-",
    /[0-9]/,
    /[0-9]/,
  ];

  emailMaskHtml = emailMask;

  informationsFormGroup: FormGroup;

  filteredAdresses: any[] = [];
  selectCPForAdresse = "";
  isLoadingAdresse = false;
  selectedAdresse = "";

  civilites = [{ value: "Monsieur" }, { value: "Madame" }];

  /*
   * Les tarifs sont maintenant :
   * - 6 € : tarif normal
   * - 2 € : tarif solidaire
   *
   * Le tableau sera ajusté dans ngOnInit selon
   * le type de personne éditée.
   */
  prices = [
    { value: "6€", label: "6 € - Tarif normal" },
    { value: "2€", label: "2 € - Tarif solidaire" },
  ];

  isWait = false;
  userInformation: any;
  inscription: any;
  isDataLoaded = false;

  phraseCheckExistAdresse =
    "Si vous ne trouvez pas votre adresse dans le moteur de recherche précédent merci de cliquer ici";

  checkNoFindAdresse = false;
  existAdresse = true;
  isSelectedAdresse = false;

  /*
   * Permet de savoir si la fiche éditée est celle
   * du demandeur principal.
   */
  isDemandeur = true;

  /*
   * Les demandeurs et conjoints doivent avoir 70 ans
   * au plus tard le 31/12/2026.
   *
   * Les aidants / accompagnants / professionnels
   * ne sont pas soumis à cette règle.
   */
  isSoumisAgeMinimum = true;

  /*
   * Date maximale de naissance :
   * 31/12/1956 inclus.
   */
  maxDateParticipant: Date;

  URL =
    environment.protocol + "://" + environment.hostName + "/inscription/load";

  isLoadJustificatif = false;
  justificatifDomicile = null;

  uploader: FileUploader = new FileUploader({
    url: this.URL,
    itemAlias: "file",
    queueLimit: 1,
    maxFileSize: 7 * 1024 * 1024,
    authToken: "Bearer " + this.authService.userValue.token,
  });

  constructor(
    private formBuilder: FormBuilder,
    private bas: BanService,
    private modalService: NgbModal,
    private toastr: ToastrService,
    private inscriptionService: InscriptionService,
    private router: Router,
    private authService: AuthService,
    private spinner: NgxSpinnerService,
    private route: ActivatedRoute,
  ) {}

  async ngOnInit(): Promise<any> {
    /*
     * Date de référence pour l'édition 2026.
     * Une personne participant en tant qu'aîné doit
     * atteindre 70 ans au plus tard le 31/12/2026.
     */
    const dateReference = moment("31/12/2026", "DD/MM/YYYY");
    const dateNaissanceMax = dateReference.clone().subtract(70, "years");

    this.maxDateParticipant = dateNaissanceMax.toDate();

    /*
     * Récupération de l'inscription.
     */
    this.inscription = await this.getInscriptionFromApi();

    /*
     * Détermination du type de fiche.
     */
    const typeInscription = this.inscription.type
      ? this.inscription.type.toLowerCase()
      : "";

    /*
     * Dans l'application actuelle, le demandeur principal
     * est enregistré comme "Seul(e)" ou "Demandeur".
     */
    this.isDemandeur =
      typeInscription === "seul(e)" || typeInscription === "demandeur";

    /*
     * Les aidants et accompagnants peuvent avoir moins de 70 ans.
     */
    this.isSoumisAgeMinimum =
      !typeInscription.includes("accompagnant") &&
      !typeInscription.includes("aidant");

    /*
     * Le tarif solidaire est proposé uniquement
     * pour le demandeur principal.
     *
     * Couple / aidant / accompagnant / professionnel :
     * tarif 6 € uniquement.
     */
    if (this.isDemandeur) {
      this.prices = [
        { value: "6€", label: "6 € - Tarif normal" },
        { value: "2€", label: "2 € - Tarif solidaire" },
      ];
    } else {
      this.prices = [{ value: "6€", label: "6 € - Tarif normal" }];
    }

    /*
     * Si on ouvre une ancienne inscription contenant encore
     * 10 €, 17 € ou 20 €, on bascule son prix affiché sur 6 €.
     */
    let priceInscription = this.inscription.price;

    if (priceInscription !== "6€" && priceInscription !== "2€") {
      priceInscription = "6€";
    }

    /*
     * Si ce n'est pas un demandeur principal,
     * aucun tarif solidaire ne doit être appliqué.
     */
    if (!this.isDemandeur && priceInscription === "2€") {
      priceInscription = "6€";
    }

    this.informationsFormGroup = this.formBuilder.group({
      id: [this.inscription._id, [Validators.required]],

      civilite: [this.inscription.civilite, [Validators.required]],

      lastName: [this.inscription.lastName, [Validators.required]],

      firstName: [this.inscription.firstName, [Validators.required]],

      dateNaissance: [this.inscription.dateNaissance, [Validators.required]],

      mail: [this.inscription.mail, [Validators.email]],

      telephone: [
        this.inscription.telephone,
        Validators.compose([
          Validators.pattern(
            "^[0-9]{2}[-]?[0-9]{2}[-]?[0-9]{2}[-]?[0-9]{2}[-]?[0-9]{2}$",
          ),
        ]),
      ],

      adresse: [
        {
          libelle: this.inscription.adresse,
        },
        Validators.compose([
          Validators.required,
          CheckMatAutoService.valueSelected(this.filteredAdresses),
        ]),
      ],

      adresseManuelle: [
        this.inscription.adresseManuelle === undefined
          ? ""
          : this.inscription.adresseManuelle,
      ],

      price: [priceInscription, [Validators.required]],

      choixListeDiffusion: [
        this.inscription.choixListeDiffusion,
        [Validators.required],
      ],

      choixPhoto: [this.inscription.choixPhoto, [Validators.required]],
    });

    /*
     * Autocomplétion de l'adresse.
     */
    this.informationsFormGroup
      .get("adresse")
      .valueChanges.pipe(
        debounceTime(300),

        tap(() => {
          this.isLoadingAdresse = true;
        }),

        switchMap((value) =>
          this.bas
            .searchAdresse({
              filterQuery: value,
              filterPostcode: this.selectCPForAdresse,
            })
            .pipe(
              finalize(() => {
                this.isLoadingAdresse = false;
              }),
            ),
        ),
      )
      .subscribe((adresses) => {
        this.filteredAdresses = adresses;
      });

    /*
     * Gestion de l'upload du justificatif.
     */
    this.uploader.onAfterAddingFile = (file) => {
      file.withCredentials = false;
    };

    this.uploader.onCompleteItem = (item: any, response: any, status: any) => {
      if (status === 200) {
        this.spinner.hide("spinner_initial");

        this.modalService.dismissAll();

        const addFile = {
          fileName: JSON.parse(response).fileName,
          location: JSON.parse(response).fileLocation,
        };

        this.justificatifDomicile = addFile;
        this.isLoadJustificatif = true;

        this.clearUploader();
      } else {
        this.spinner.hide("spinner_initial");
      }
    };

    /*
     * Gestion adresse trouvée / adresse manuelle.
     */
    if (
      this.inscription.existAdresse ||
      this.inscription.existAdresse === undefined
    ) {
      this.checkNoFindAdresse = false;
      this.existAdresse = true;

      this.informationsFormGroup.get("adresse").disable();

      this.phraseCheckExistAdresse =
        "Si vous ne trouvez pas votre adresse dans le moteur de recherche précédent merci de cliquer ici";
    } else {
      this.existAdresse = false;
      this.checkNoFindAdresse = true;

      this.informationsFormGroup.controls["adresse"].clearValidators();

      this.informationsFormGroup.controls["adresse"].updateValueAndValidity();

      this.phraseCheckExistAdresse =
        "Décocher pour revenir au moteur de recherche de l'adresse";
    }

    /*
     * Chargement d'un éventuel justificatif existant.
     */
    if (this.inscription.existJustificatif) {
      this.isLoadJustificatif = true;

      const addFile = {
        fileName: this.inscription.justificatifFileName,
        location: this.inscription.justificatifLocation,
      };

      this.justificatifDomicile = addFile;
    }

    this.isDataLoaded = true;
  }

  getInscriptionFromApi(): Promise<Inscription> {
    return new Promise<Inscription>(async (resolve, reject) => {
      this.route.params.subscribe(async (params) => {
        try {
          const inscription = await this.inscriptionService
            .getInscription(params.id)
            .toPromise();

          resolve(inscription);
        } catch (error) {
          reject(error);
        }
      });
    });
  }

  uploadJustificatif() {
    this.inscriptionService
      .viewPJ(this.justificatifDomicile.location)
      .subscribe((response: any) => {
        const file = response.file;

        const linkFile = `data:file;base64,${file}`;

        const downloadLink = document.createElement("a");

        const fileName = this.justificatifDomicile.fileName;

        downloadLink.href = linkFile;
        downloadLink.download = fileName;

        downloadLink.click();
      });
  }

  deleteJustificatif() {
    this.justificatifDomicile = null;
    this.isLoadJustificatif = false;

    this.clearUploader();
  }

  clearUploader() {
    while (this.uploader.queue.length) {
      this.uploader.queue[0].remove();
    }

    this.uploader.progress = 0;
  }

  uploadFile() {
    this.spinner.show("spinner_initial");
  }

  openModalUpload(modalUploadFile) {
    this.modalService
      .open(modalUploadFile, {
        ariaLabelledBy: "modal-basic-title",
        size: "lg",
      })
      .result.then(
        (result) => {
          // Fermeture normale de la fenêtre.
        },
        (reason) => {
          // Fermeture de la fenêtre sans action.
        },
      );
  }

  isExistAdresse(event) {
    this.existAdresse = !event.checked;

    this.phraseCheckExistAdresse = !event.checked
      ? "Si vous ne trouvez pas votre adresse dans le moteur de recherche précédent merci de cliquer ici"
      : "Décocher pour revenir au moteur de recherche de l'adresse";

    if (!event.checked) {
      this.isSelectedAdresse = false;

      this.informationsFormGroup.get("adresseManuelle").setValue("");

      this.informationsFormGroup.controls["adresseManuelle"].clearValidators();

      this.informationsFormGroup.controls[
        "adresseManuelle"
      ].updateValueAndValidity();

      this.informationsFormGroup.get("adresse").setValue("");

      this.informationsFormGroup.controls["adresse"].setValidators(
        Validators.compose([
          Validators.required,
          CheckMatAutoService.valueSelected([]),
        ]),
      );

      this.informationsFormGroup.controls["adresse"].updateValueAndValidity();

      this.informationsFormGroup.get("adresse").enable();
    } else {
      this.isSelectedAdresse = true;

      this.informationsFormGroup.get("adresseManuelle").setValue("");

      this.informationsFormGroup.controls["adresseManuelle"].setValidators([
        Validators.required,
      ]);

      this.informationsFormGroup.controls[
        "adresseManuelle"
      ].updateValueAndValidity();

      this.informationsFormGroup.get("adresse").setValue("");

      this.informationsFormGroup.controls["adresse"].clearValidators();

      this.informationsFormGroup.controls["adresse"].updateValueAndValidity();
    }
  }

  displayAdresse(adresse: any) {
    if (adresse) {
      return adresse.libelle;
    }
  }

  getAdresse(selectedAdresse) {
    this.informationsFormGroup.get("adresse").disable();

    this.isSelectedAdresse = true;
  }

  resetAdresse() {
    this.informationsFormGroup.get("adresse").setValue("");

    this.informationsFormGroup.get("adresse").enable();

    this.isSelectedAdresse = false;
  }

  editInscription() {
    /*
     * Vérification de la règle des 70 ans.
     *
     * Elle ne s'applique pas aux aidants et accompagnants.
     */
    if (
      this.isSoumisAgeMinimum &&
      moment(this.informationsFormGroup.controls.dateNaissance.value).isAfter(
        moment("31/12/1956", "DD/MM/YYYY"),
        "day",
      )
    ) {
      this.toastr.warning(
        "La personne doit avoir 70 ans au plus tard le 31 décembre 2026.",
      );

      return;
    }

    /*
     * Le demandeur doit avoir au moins
     * un moyen de contact.
     */
    if (
      this.isDemandeur &&
      (this.informationsFormGroup.controls.mail.value === "" ||
        this.informationsFormGroup.controls.mail.value === null) &&
      (this.informationsFormGroup.controls.telephone.value === "" ||
        this.informationsFormGroup.controls.telephone.value === null)
    ) {
      this.toastr.warning(
        "Vous devez renseigner au moins un contact (mail ou téléphone)",
      );

      return;
    }

    /*
     * Sécurisation de l'adresse.
     */
    let adresse = "";

    const adresseValue = this.informationsFormGroup.controls.adresse.value;

    if (
      adresseValue &&
      typeof adresseValue === "object" &&
      adresseValue.libelle
    ) {
      adresse = adresseValue.libelle;
    } else if (typeof adresseValue === "string") {
      adresse = adresseValue;
    }

    const dataSend = {
      _id: this.informationsFormGroup.controls.id.value,

      civilite: this.informationsFormGroup.controls.civilite.value,

      firstName: this.informationsFormGroup.controls.firstName.value,

      lastName: this.informationsFormGroup.controls.lastName.value,

      dateNaissance: this.informationsFormGroup.controls.dateNaissance.value,

      mail: this.informationsFormGroup.controls.mail.value,

      telephone: this.informationsFormGroup.controls.telephone.value,

      adresse: adresse,

      adresseManuelle:
        this.informationsFormGroup.controls.adresseManuelle.value,

      existAdresse: this.existAdresse,

      price: this.informationsFormGroup.controls.price.value,

      choixListeDiffusion:
        this.informationsFormGroup.controls.choixListeDiffusion.value,

      choixPhoto: this.informationsFormGroup.controls.choixPhoto.value,

      existJustificatif: this.justificatifDomicile !== null,

      justificatifFileName:
        this.justificatifDomicile !== null
          ? this.justificatifDomicile.fileName
          : "",

      justificatifLocation:
        this.justificatifDomicile !== null
          ? this.justificatifDomicile.location
          : "",
    };

    this.inscriptionService
      .editInscription(dataSend)
      .subscribe((response: any) => {
        if (response.Inscription === "Inscription updated successfully") {
          this.isWait = false;

          this.spinner.hide();

          this.toastr.success("Inscription modifiée avec succès");

          this.router.navigate(["/inscription/list"]);
        } else {
          this.isWait = false;

          this.spinner.hide();

          this.toastr.warning("Le serveur a rencontré un problème");
        }
      });
  }
}
